import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import styled from 'styled-components/native';
import { FlatList } from 'react-native';
import _ from 'lodash';
import {
  ScreenContainer,
  ContentContainer,
  FullScreenContainer,
  LogoWrapper,
  StyledHoverButton,
  Card,
  InputContainer,
  StyledHeadline
} from '../styles';

import {
  FocusAwareStatusBar,
  TextInput,
  LogoHeader,
  Alert,
  AlertHandle,
  Body,
  HoverButton
} from 'src/components';
import { AlertOption } from 'src/components/Alert';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import { Size } from 'src/constants';
import { Weight } from 'src/theme';

import { findUsers, impersonateUser } from 'src/api/admin';
import UserSearchResult from 'src/interfaces/api/UserSearchResult';
import UserSearchResultItem from './UserSearchResultItem';

interface Props {
  endSession: () => void;
  token: string;
  startImpersonateSession: (userToken: string) => void;
}

const UserSearch: React.FC<Props> = ({ endSession, token, startImpersonateSession }) => {
  const { t } = useTranslation('impersonate');
  const { colors } = useAppTheme();

  const [petOwnerEmail, setPetOwnerEmail] = useState<string>();
  const modalRef = useRef<AlertHandle>(null);

  const { mutate: impersonate } = useMutation({
    mutationFn: async (searchResult: UserSearchResult) =>
      await impersonateUser(searchResult.id, token),
    mutationKey: ['impersonate', token],
    onSuccess: (data) => {
      startImpersonateSession(data.token);
    }
  });

  const {
    data: searchResults = [],
    mutateAsync: find,
    isSuccess,
    isLoading
  } = useMutation({
    mutationKey: 'users-find',
    mutationFn: async () => {
      if (petOwnerEmail && token) {
        const response = await findUsers(petOwnerEmail, token);
        if (!response.data.length) modalRef.current?.alert();
        return response.data;
      }
      return [];
    }
  });

  const options: AlertOption[] = useMemo(() => [{ label: t('common:OK') }], [t]);

  return (
    <ScreenContainer>
      <LogoWrapper>
        <LogoHeader displayName={t('login:appName')} />
      </LogoWrapper>
      <FullScreenContainer>
        <FocusAwareStatusBar barStyle='light-content' backgroundColor={colors.primary} />
        <ContentContainer>
          <Card>
            <StyledHeadline>{t('userSearch')}</StyledHeadline>

            <InputContainer>
              <TextInput
                label={t('petOwnerEmail')}
                value={petOwnerEmail}
                onChangeText={setPetOwnerEmail}
                autoCapitalize='none'
                onSubmitEditing={async () => await find()}
              />
            </InputContainer>

            <FlatList<UserSearchResult>
              ListEmptyComponent={() =>
                _.isEmpty(searchResults) &&
                isSuccess && (
                  <Body fontWeight={Weight.MEDIUM} textAlign='center' color={colors.onPrimary}>
                    {t('noUsersFound')}
                  </Body>
                )
              }
              data={searchResults}
              renderItem={({ item, index }) => (
                <UserSearchResultItem
                  isFirst={index === 0}
                  isLast={index === searchResults.length - 1}
                  key={index}
                  searchResult={item}
                  impersonate={impersonate}
                />
              )}
              contentContainerStyle={{
                marginTop: Size.S,
                flexGrow: 1
              }}
            />

            <StyledHoverButton
              mode='contained'
              onPress={async () => find()}
              labelStyle={{ color: colors.onPrimary }}
              uppercase
              disabled={isLoading}
            >
              {t('findUsers')}
            </StyledHoverButton>
            <Alert
              ref={modalRef}
              title={t('noUsersFound')}
              body={t('noUsersFoundMessage')}
              options={options}
            />
          </Card>
          <EndSessionButton onPress={endSession}>{t('endSession')}</EndSessionButton>
        </ContentContainer>
      </FullScreenContainer>
    </ScreenContainer>
  );
};

export default UserSearch;

const EndSessionButton = styled(HoverButton).attrs(({ theme }) => ({
  mode: 'text',
  labelStyle: {
    color: theme.colors.surface
  }
}))`
  margin-top: ${Size.XL}px;
`;
